/**
 * Three glass cards above the heatmap canvas:
 *   1. Current — spot, structural read, multi-horizon regime chips.
 *   2. Magnets & Amps — strongest pin walls (positive GEX) and cascade
 *      walls (negative GEX) above and below spot. Front-week vs
 *      full-chain views toggle inline at the top of the card so users
 *      can compare without losing screen space to two separate panels.
 *   3. Levels — top King and Gatekeeper strikes with $ amounts.
 *
 * All numbers come from `frame.analytics` (computed backend-side in
 * `engine/analytics.py`). When a magnet/amp comes back null (no
 * significant strike in that quadrant — e.g., monolithic regime with
 * no opposite-sign walls) we render an explicit "no significant
 * strike" rather than a bare "—" so the empty state has meaning.
 *
 * Toggle state for the Magnets card is persisted to localStorage
 * (gv2.magnetMode) so power users land back on whichever view they
 * were last using.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HeatmapFrame, StrikeRef, FrameAnalytics } from '../../lib/types';
import { formatCompact, formatStrike } from '../../lib/format';
import { STORAGE_KEYS } from '../../lib/constants';

interface HeatmapPanelsProps {
  frame: HeatmapFrame | null;
}

type MagnetMode = 'front' | 'full';

const MAGNET_MODE_KEY = STORAGE_KEYS.magnetMode;

function readMagnetMode(): MagnetMode {
  try {
    const v = localStorage.getItem(MAGNET_MODE_KEY);
    return v === 'full' ? 'full' : 'front';
  } catch { return 'front'; }
}

// ── helpers ───────────────────────────────────────────

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(2)}%`;
}

function regimeTone(label: string): 'pos' | 'neg' | 'neutral' {
  if (/SHORT|NEGATIVE|AMPLIFY|BEARISH/i.test(label)) return 'neg';
  if (/LONG|DAMPEN|BULLISH/i.test(label)) return 'pos';
  return 'neutral';
}

interface StrikeRowProps {
  label: string;
  refStrike: StrikeRef | null;
  spot: number;
  variant: 'magnet' | 'amp';
}

const StrikeRow: React.FC<StrikeRowProps> = ({ label, refStrike, spot, variant }) => {
  if (!refStrike) {
    return (
      <div className="spot-row">
        <span className="spot-label">{label}</span>
        <span className="spot-value spot-value--empty" title="No significant strike of this type in this view">
          none
        </span>
      </div>
    );
  }
  const distancePct = ((refStrike.strike - spot) / spot) * 100;
  return (
    <div className="spot-row">
      <span className="spot-label">{label}</span>
      <span className={`spot-value ${variant === 'magnet' ? 'pos' : 'neg'}`}>
        ${formatStrike(refStrike.strike)}
        <span className="lvl-distance">{distancePct >= 0 ? '+' : ''}{distancePct.toFixed(2)}%</span>
        <span className="lvl-distance lvl-distance--value">{formatCompact(refStrike.value)}</span>
      </span>
    </div>
  );
};

// Pick which set of magnet/amp refs we render based on the toggle.
function pickRefs(a: FrameAnalytics, mode: MagnetMode) {
  if (mode === 'front') {
    return {
      magnet_above: a.fw_magnet_above,
      magnet_below: a.fw_magnet_below,
      amp_above: a.fw_amp_above,
      amp_below: a.fw_amp_below,
      net: a.front.net,
      max_abs: a.front.max_abs,
    };
  }
  return {
    magnet_above: a.magnet_above,
    magnet_below: a.magnet_below,
    amp_above: a.amp_above,
    amp_below: a.amp_below,
    net: a.full.net,
    max_abs: a.full.max_abs,
  };
}

const HeatmapPanels: React.FC<HeatmapPanelsProps> = ({ frame }) => {
  const [magnetMode, setMagnetMode] = useState<MagnetMode>(() => readMagnetMode());
  const setMode = useCallback((mode: MagnetMode) => {
    setMagnetMode(mode);
    try { localStorage.setItem(MAGNET_MODE_KEY, mode); } catch { /* noop */ }
  }, []);

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== MAGNET_MODE_KEY) return;
      setMagnetMode(readMagnetMode());
    };
    globalThis.addEventListener('storage', onStorage);
    return () => globalThis.removeEventListener('storage', onStorage);
  }, []);

  const data = useMemo(() => {
    if (!frame) return null;

    const a = frame.analytics ?? null;
    const spot = frame.spot_price;
    const flip = frame.gex_flip_point;
    const flipDist = flip && spot > 0 ? (spot - flip) / spot : null;

    const king = frame.gex_nodes.find(n => n.node_type === 'king') || null;
    const counterKing = frame.gex_nodes.find(n => n.node_type === 'counter_king') || null;
    const gk = frame.gex_nodes.find(n => n.node_type === 'gatekeeper') || null;
    const airCount = frame.gex_air_pockets?.length ?? 0;

    return { a, spot, flip, flipDist, king, counterKing, gk, airCount };
  }, [frame]);

  if (!frame || !data) {
    return (
      <div className="gv-panels gv-panels--loading">
        <div className="gv-panel">
          <div className="gv-panel-title">Current</div>
          <div className="spot-row"><span className="spot-label">Loading…</span></div>
        </div>
      </div>
    );
  }

  const { a, spot, flip, flipDist, king, counterKing, gk, airCount } = data;
  const refs = a ? pickRefs(a, magnetMode) : null;

  return (
    <div className="gv-panels">
      {/* Current ── structural read + multi-horizon regimes */}
      <div className="gv-panel">
        <div className="gv-panel-title">Current</div>
        <div className="spot-row">
          <span className="spot-label">Spot</span>
          <span className="spot-value cyan">${spot.toFixed(2)}</span>
        </div>
        <div className="spot-row">
          <span className="spot-label">Flip</span>
          <span className="spot-value">{flip != null ? `$${formatStrike(flip)}` : '—'}</span>
        </div>
        <div className="spot-row">
          <span className="spot-label">Distance</span>
          <span className="spot-value">{fmtPct(flipDist)}</span>
        </div>
        {a ? (
          <>
            <div className="spot-row spot-row--regime">
              <span className="spot-label">Front</span>
              <span className={`spot-value chip-${regimeTone(a.front.regime)}`}>{a.front.regime}</span>
            </div>
            <div className="spot-row spot-row--regime">
              <span className="spot-label">Full</span>
              <span className={`spot-value chip-${regimeTone(a.full.regime)}`}>{a.full.regime}</span>
            </div>
            <div className="spot-row spot-row--regime">
              <span className="spot-label">Structural</span>
              <span className={`spot-value chip-${regimeTone(a.structural.regime)}`}>{a.structural.regime}</span>
            </div>
            {a.structural_note && (
              <p className="gv-panel-note">{a.structural_note}</p>
            )}
          </>
        ) : (
          <div className="spot-row">
            <span className="spot-label">Regime</span>
            <span className="spot-value">
              {flip == null ? '—' : (spot >= flip ? 'POSITIVE' : 'NEGATIVE')}
            </span>
          </div>
        )}
      </div>

      {/* Magnets & Amps — combined panel with FRONT/FULL toggle */}
      <div className="gv-panel">
        <div className="gv-panel-title-row">
          <div className="gv-panel-title">Magnets &amp; Amps</div>
          <div className="gv-mode-toggle" role="group" aria-label="Magnets and Amps view">
            <button
              type="button"
              className={`gv-mode-btn${magnetMode === 'front' ? ' active' : ''}`}
              onClick={() => setMode('front')}
              aria-pressed={magnetMode === 'front'}
            >
              Front Week
            </button>
            <button
              type="button"
              className={`gv-mode-btn${magnetMode === 'full' ? ' active' : ''}`}
              onClick={() => setMode('full')}
              aria-pressed={magnetMode === 'full'}
            >
              Full Chain
            </button>
          </div>
        </div>
        {a && refs ? (
          <>
            <StrikeRow label="Magnet ↑" refStrike={refs.magnet_above} spot={spot} variant="magnet" />
            <StrikeRow label="Magnet ↓" refStrike={refs.magnet_below} spot={spot} variant="magnet" />
            <StrikeRow label="Amp ↑"    refStrike={refs.amp_above}    spot={spot} variant="amp" />
            <StrikeRow label="Amp ↓"    refStrike={refs.amp_below}    spot={spot} variant="amp" />
            <div className="spot-row">
              <span className="spot-label">{magnetMode === 'front' ? 'Front Net' : 'Full Net'}</span>
              <span className={`spot-value ${refs.net >= 0 ? 'pos' : 'neg'}`}>
                {formatCompact(refs.net)}
              </span>
            </div>
            <div className="spot-row">
              <span className="spot-label">Max |GEX|</span>
              <span className="spot-value">{formatCompact(refs.max_abs)}</span>
            </div>
          </>
        ) : (
          <div className="spot-row"><span className="spot-label">No analytics yet</span></div>
        )}
      </div>

      {/* Levels — King + Counter-King + Gatekeeper + air pockets */}
      <div className="gv-panel">
        <div className="gv-panel-title">Levels</div>
        {/* King is the FRONT (closest) expiration's dominant gamma node.
            Negative (short-gamma) King renders in the purple day-low
            family so its sign reads at a glance — matching the heatmap
            row treatment. */}
        <div className="spot-row">
          <span
            className="spot-label"
            style={king && !king.is_positive ? { color: 'var(--hm-day-low-border)' } : undefined}
          >
            King ★
          </span>
          <span
            className="spot-value"
            style={king && !king.is_positive ? { color: 'var(--hm-day-low-border)' } : undefined}
          >
            {king ? (
              <>
                ${formatStrike(king.strike)}
                <span className="lvl-distance lvl-distance--value">{formatCompact(king.value)}</span>
              </>
            ) : '—'}
          </span>
        </div>
        <div className="spot-row">
          <span className="spot-label" style={{ color: 'var(--hm-counter-king, var(--red))' }}>
            Counter-King <span aria-label="Counter-King (largest opposite-sign GEX node)">✦</span>
          </span>
          <span className="spot-value" style={{ color: 'var(--hm-counter-king, var(--red))' }}>
            {counterKing ? (
              <>
                ${formatStrike(counterKing.strike)}
                <span className="lvl-distance lvl-distance--value">{formatCompact(counterKing.value)}</span>
              </>
            ) : (
              <span className="spot-value--empty" title="No significant opposite-sign strike in current chain">none</span>
            )}
          </span>
        </div>
        <div className="spot-row">
          <span className="spot-label">Gatekeeper ◆</span>
          <span className="spot-value">
            {gk ? (
              <>
                ${formatStrike(gk.strike)}
                <span className="lvl-distance lvl-distance--value">{formatCompact(gk.value)}</span>
              </>
            ) : '—'}
          </span>
        </div>
        <div className="spot-row">
          <span className="spot-label">Air Pockets</span>
          <span className="spot-value">{airCount}</span>
        </div>
      </div>
    </div>
  );
};

export default HeatmapPanels;
